/* eslint-disable no-unused-vars */
import { Link } from "@mui/material";
import { OVERDUE_STATUSES, OverdueStatus } from "../../config/params";
/* eslint-disable max-len */
import React, { useEffect, useState } from "react";
import { fromUnixToFormattedVisitdateStr, hasEndDateElapsed, isDateWithinFiveDays, isWithinPast30Days } from "../../utils/unix-timestamp";

import CentralLoadingIndicator from "../CentralLoadingIndicator/CentralLoadingIndicator";
import { IPetItemResponse } from "../../services/pet-list/pet-list.interface";
import { IWellnessPlanDetails } from "../../services/wellness-plan/wellness-plan.interface";
import { MIXPANEL_EVENTS } from "../../config/mixPanelEvents";
import MemberCardIcon from "../Icons/MemberCardIcon";
import { PAGES_PATH } from "../../navigation/params";
import { getScratchPayOverdueSubtitle } from "../../utils/plan-overdue-status";
import { useFetchWellnessPlanByPetId } from "../../services/wellness-plan/wellness-plan.service";
import { useNavigate } from "react-router-dom";
import { userActivityLogger } from "../../utils/mixpanel";

export const getPlanOverdueText = (overdueStatus: string, isRenewUnsuccessful?: boolean, isCancelledRenewUnsuccessful?: boolean, isPlanEndingInFiveDays?: boolean) => {
  switch (overdueStatus) {
  case OVERDUE_STATUSES.OVERDUE:
    return "Wellness Plan Payment Overdue";
  case OVERDUE_STATUSES.PAUSED:
    return isPlanEndingInFiveDays ?
      "Payment Overdue" :
      isRenewUnsuccessful ? "Renewal Unsuccessful" : "Wellness Plan Paused";
  case OVERDUE_STATUSES.CANCELLED:
    return isCancelledRenewUnsuccessful ? "Renewal Unsuccessful" : "Wellness Plan Canceled for Non-Payment";
  default:
    return "";
  }
};

interface IPlanCard {
  petList: IPetItemResponse[];
  handlePetChange?: (petId: string, category: string) => void;
  petData: IPetItemResponse;
  isSubscriptionBenefitAvailable?: boolean;
  planOverdueStatus: OverdueStatus | null;
  setIsLoading?: (loading: boolean) => void;
  totalSavings: number
  planName: string
  examsCount: number;
  hasWellnessPlan?: boolean
  isScratchpay?: boolean
}

export const getWellnessMemberList = (petDataList: IPetItemResponse[], currentPetId?: string) => {
  const pet = petDataList.filter((pet) => pet?.items?.wellnessPlanStatus && pet.id !== currentPetId);

  return pet;
};

export const WellnessMemberPlanCard = (props: IPlanCard) => {
  const { petData, planOverdueStatus, setIsLoading, totalSavings, planName, examsCount, isScratchpay } = props;
  const navigate = useNavigate();
  const { getWellnessPlanByPetId, data, loading } = useFetchWellnessPlanByPetId();
  const [petWellnessDetails, setWellnessPetDetails] = useState<IWellnessPlanDetails>();

  const isScratchPayDelinquency = Boolean(isScratchpay) && Boolean(planOverdueStatus);
  const isScratchPayLocked = Boolean(isScratchpay) && planOverdueStatus === OVERDUE_STATUSES.PAUSED;
  const isScratchPayPaymentOverdue = isScratchpay && planOverdueStatus === OVERDUE_STATUSES.OVERDUE;
  const endDate = petWellnessDetails?.endDate;
  const nextBillingDate = petWellnessDetails?.nextBillingDate;
  const isMonthlyPlan = isScratchpay || data?.wellnessPlan?.[0]?.billingInterval === "month";
  const titleText = `${petData.name}'s ${planName}`;
  const overdueStatusClassName = (() => {
    if (!planOverdueStatus || !petWellnessDetails) return "";
    if (isScratchPayPaymentOverdue) return "text-red-4 bg-red-3";
    if (planOverdueStatus === OVERDUE_STATUSES.PAUSED) return "text-white bg-red-4";
    if (planOverdueStatus === OVERDUE_STATUSES.OVERDUE) return "text-red-4 bg-red-3";
    if (
      planOverdueStatus === OVERDUE_STATUSES.CANCELLED ||
      hasEndDateElapsed(petWellnessDetails?.endDate) ||
      (isWithinPast30Days(petWellnessDetails?.startDate) && petWellnessDetails.renewalStatus)
    ) {
      return "text-white bg-red-4";
    }
    return "text-red-4 bg-red-3";
  })();

  useEffect(() => {
    petData && getWellnessPlanByPetId(petData.id);
  }, [petData]);

  useEffect(() => {
    if (data) {
      setWellnessPetDetails(data?.wellnessPlanDetails[0]);
    }
  }, [data]);

  useEffect(() => {
    if (!isScratchpay) return;
    if (planOverdueStatus !== OVERDUE_STATUSES.OVERDUE && planOverdueStatus !== OVERDUE_STATUSES.PAUSED) return;

    userActivityLogger(MIXPANEL_EVENTS.DELINQUENCY_SURFACE_SHOWN, {
      provider: "scratchpay",
      delinquency_state: planOverdueStatus === OVERDUE_STATUSES.OVERDUE ? "overdue_notice" : "paused",
      entry_point: "care_portal",
      surface: "banner",
      pet_id: petData.id,
      pet_name: petData.name,
      plan_name: planName,
      is_scratchpay: true,
      payment_processor: "scratchpay",
      card_type: planOverdueStatus === OVERDUE_STATUSES.OVERDUE ? "overdue" : "benefits_paused",
      has_payment_url: false,
      payment_url_source: "none",
      referred_from_page: "care_portal",
    });
  }, [isScratchpay, planOverdueStatus, petData.id, petData.name, planName]);

  useEffect(() => {
    setIsLoading && setIsLoading(loading);
  }, [loading]);

  if (loading) {
    return <CentralLoadingIndicator />;
  }

  return (
    <>
      <div className={`relative bg-violet-1 ${planOverdueStatus ? "rounded-xl" : "rounded-xl shadow-lg"} overflow-hidden p-6`}>
        <MemberCardIcon className="absolute top-0 right-0 h-full w-auto pointer-events-none" />
        <div className="relative z-10 flex flex-col items-center gap-2 ">
          <div className="text-2xl text-white font-semibold text-center">
            {titleText}
          </div>
          {(planOverdueStatus) && petWellnessDetails &&
            <div className={`w-full text-center text-sm font-semibold rounded py-0.5 ${overdueStatusClassName} z-10`}>
              {getPlanOverdueText(planOverdueStatus,
                hasEndDateElapsed(petWellnessDetails?.endDate),
                (isWithinPast30Days(petWellnessDetails?.startDate) && petWellnessDetails.renewalStatus),
                typeof endDate === "string" ? isDateWithinFiveDays(endDate) : false)
              }
            </div>}
          {isScratchPayDelinquency && (
            <div className="text-xs text-white text-center mt-1 max-w-[34rem]">
              {planOverdueStatus && getScratchPayOverdueSubtitle(planOverdueStatus)}
            </div>
          )}
          {isMonthlyPlan && !planOverdueStatus && nextBillingDate &&
                <div className="text-xs text-white">
                  Next billing on: {fromUnixToFormattedVisitdateStr(+nextBillingDate)}
                </div>
          }
          {(planOverdueStatus !== OVERDUE_STATUSES.PAUSED && planOverdueStatus !== OVERDUE_STATUSES.CANCELLED) && endDate &&
            <div className="text-xs text-white">
              Plan ends on: {fromUnixToFormattedVisitdateStr(+endDate)}
            </div>
          }
          {!isScratchPayLocked && (
            <div className="bg-white rounded-lg px-6 py-3 mt-1 flex items-center justify-center gap-8 min-w-[285px]">
              <div className="flex flex-col items-center gap-1 text-center min-w-[2rem]">
                <span className="text-lg font-semibold text-black">{examsCount}</span>
                <span className="text-[10px] text-black ">Exams</span>
              </div>
              <div className="w-px h-8 bg-gray-3" />
              <div className="flex flex-col items-center gap-1 text-center">
                <span className="text-lg font-semibold text-black">${totalSavings ? totalSavings.toFixed(2) : "0.00"}</span>
                <span className="text-[10px] text-black">Savings</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default WellnessMemberPlanCard;
